<template>
  <div>
    <div
      class="aeb-form-field"
      :class="{
        focused: isFocused,
        'has-value': hasValue,
        error: isTouched && errors.length > 0,
      }"
    >
      <input
        :type="type"
        :value="modelValue"
        @input="setModelValue($event)"
        @blur="setFocused(false)"
        @focus="setFocused(true)"
        class="aeb-input"
      />
      <div class="aeb-label">
        <slot name="label" />
      </div>
      <div class="aeb-icon">
        <slot name="icon" />
      </div>
      <div class="aeb-form-field__errors"><slot name="errors"> </slot></div>
    </div>
    <div class="error-message" v-if="isTouched && errors.length > 0">
      <span v-for="error of errors" :key="error.$uid">
        {{ error.$message }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    required: false,
    default: 'text',
  },
  prefix: {
    type: String,
    required: false,
    default: '',
  },
  errors: {
    type: Array,
    required: false,
    default: [],
  },
});

const emit = defineEmits(['update:modelValue']);

const isTouched = ref(false);
const isFocused = ref(false);
const hasValue = computed(() => props.modelValue.trim());

const setFocused = (focused: boolean) => {
  isFocused.value = focused;

  if (!focused) {
    isTouched.value = true;
  }

  if (props.prefix) {
    focused ? onApplyPrefix() : onClearPrefix();
  }
};

const onApplyPrefix = () => {
  if (!props.modelValue) {
    emit('update:modelValue', props.prefix);
  }
};

const normalizeDigits = (value: string) => value.replace(/\D/g, '');

const onClearPrefix = () => {
  const valueDigits = normalizeDigits(props.modelValue);
  const prefixDigits = normalizeDigits(props.prefix);

  if (!valueDigits || valueDigits === prefixDigits) {
    emit('update:modelValue', '');
  }
};

const setModelValue = (event: InputEvent) => {
  emit('update:modelValue', (event.target as HTMLInputElement).value);
};
</script>

<style lang="scss" scoped>
$input-height: 56px;

.error-message {
  color: #ff3055;
  margin-left: 8px;
  margin-top: 4px;
  font-size: 12px;
}

.aeb-form-field {
  position: relative;
  display: flex;
  color: $darkblue;

  textarea {
    color: $darkblue;
    &.aeb-input {
      padding: 16px;
      font-family: 'Roboto', 'Helvetica Neue', sans-serif;
    }
  }

  .aeb-icon {
    position: absolute;
    right: 18px;
    top: 20px;
    width: 16px;
    height: 16px;

    img {
      width: 16px;
      height: 16px;
    }
  }

  .aeb-input {
    border: 2px solid $gray-primary;
    box-sizing: border-box;
    border-radius: 10px;
    height: $input-height;
    width: 100%;
    padding: 0 16px;
    font-size: 18px;
    color: $darkblue;
    caret-color: $blue;

    &:focus {
      outline: none;
    }

    &:disabled {
      background-color: $white;
      border: 2px solid $gray-primary;
    }

    &,
    &::-webkit-search-cancel-button,
    &::-webkit-search-decoration,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      -webkit-appearance: none;
    }

    &::-webkit-contacts-auto-fill-button,
    &::-webkit-caps-lock-indicator,
    &::-webkit-credentials-auto-fill-button {
      visibility: hidden;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &[type='number'] {
      -moz-appearance: textfield;
    }

    &::-webkit-input-placeholder {
      font-family: 'Roboto', 'Helvetica Neue', sans-serif;
    }

    &::-webkit-datetime-edit {
      font-family: 'Roboto', 'Helvetica Neue', sans-serif;
    }
  }

  .aeb-label {
    position: absolute;
    left: 10px;
    top: 28px;
    transform: translate(0, -50%);
    font-size: 18px;
    background-color: #fff;
    padding: 6px;
    border-radius: 4px;
    pointer-events: none;
    color: $gray-primary;
    transition: all cubic-bezier(0.4, 0, 0.2, 1) 200ms;
  }

  &.focused {
    .aeb-input {
      border-color: #2f88f0;
    }

    .aeb-label {
      transform: translate(0, calc($input-height / -2 + -50%));
      font-size: 12px;
      color: #2f88f0;
    }
  }

  &.has-value {
    .aeb-label {
      transform: translate(0, calc($input-height / -2 + -50%));
      font-size: 12px;
    }
  }

  &.error {
    .aeb-input {
      border-color: #ff3055;
    }

    .aeb-label {
      color: #ff3055;
    }
  }

  &.center {
    .aeb-input {
      text-align: center;
    }

    .aeb-label {
      left: 50%;
      transform: translate(-50%, -50%);
      white-space: nowrap;
    }

    & + .error-message {
      margin-left: 0;
    }

    &.focused {
      .aeb-label {
        transform: translate(-50%, calc($input-height / -2 + -50%));
      }
    }

    &.has-value {
      .aeb-label {
        transform: translate(-50%, calc($input-height / -2 + -50%));
      }
    }
  }

  &--svoi {
    &.focused {
      .aeb-label {
        transform: translate(3px, calc(($input-height / 2) - 52px));
        background-color: transparent;
        color: #94a8be;
      }
    }

    .aeb-input {
      font-size: 14px !important;
      padding: 25px 18px 9px 18px !important;
    }

    &.has-value {
      .aeb-label {
        transform: translate(3px, calc(($input-height / 2) - 52px));
      }
    }
  }
}
</style>
